import React, { useState, useEffect, useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Loader, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ImageZoomModal } from '@/components/ui/image-zoom-modal';
import { whatsappMediaService } from '@/services/whatsappMediaService';
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';
import { useToast } from '@/hooks/use-toast';
import { FormattedMessageContent } from '@/components/chat/FormattedMessageContent';
import type { Message } from '@/types/message-types';

interface MediaImageProps {
  message: Message;
  tenantId: string;
}

function MediaImageComponent({ message, tenantId }: MediaImageProps) {
  const { t } = useTranslation('conversations');
  const componentIdRef = useRef(`img-${message.id}`);
  const loadAttemptRef = useRef(0);
  const mountedRef = useRef(true);

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showZoomModal, setShowZoomModal] = useState(false);
  const { toast } = useToast();

  // Component cleanup tracking and error recovery
  useEffect(() => {
    mountedRef.current = true;
    
    // Add window error listener for postMessage errors that cause image loss
    const handleWindowError = (event: ErrorEvent) => {
      if (event.message?.includes('postMessage') && imageUrl) {
        loadImage(true);
      }
    };
    
    window.addEventListener('error', handleWindowError);
    
    return () => {
      mountedRef.current = false;
      window.removeEventListener('error', handleWindowError);
    };
  }, [imageUrl]);

  const loadImage = async (forceReload = false) => {
    loadAttemptRef.current += 1;
    const attemptId = loadAttemptRef.current;

    if (!mountedRef.current) {
      return;
    }

    setLoading(true);
    if (forceReload) {
      setError(null);
      setImageUrl(null);
      setThumbnailUrl(null);
    }

    // Debug logging for customer media issue
    console.log('🖼️ MediaImage loadImage:', {
      messageId: message.id,
      senderType: message.sender_type,
      hasMediaUrl: !!message.media_url,
      mediaUrlValue: message.media_url,
      mediaUrlLength: message.media_url?.length,
      metadata: message.metadata,
      forceReload
    });

    // First check if media_url is already available in the message
    // Resolve storage paths to signed URLs (external URLs pass through)
    if (message.media_url?.trim() && !forceReload) {
      const resolved = await whatsappMediaService.resolveMediaUrl(message.media_url);
      if (mountedRef.current && resolved) {
        console.log('✅ MediaImage: Using resolved media_url');
        setImageUrl(resolved);
        setLoading(false);
      }
      return;
    }

    // Check for AI-sent images with direct URLs in metadata (fallback for existing messages)
    // This handles product images sent via custom functions that have URL in metadata.image_url
    // IMPORTANT: Parse metadata if it's a string (database returns stringified JSON)
    let parsedMetadata = message.metadata;
    if (typeof message.metadata === 'string') {
      try {
        parsedMetadata = JSON.parse(message.metadata);
        console.log('🔧 MediaImage: Parsed metadata from string:', parsedMetadata);
      } catch (e) {
        console.error('❌ MediaImage: Failed to parse metadata string:', e);
        parsedMetadata = null;
      }
    }

    if (parsedMetadata?.image_url && !forceReload) {
      console.log('✅ MediaImage: Using image_url from metadata (AI-sent image):', parsedMetadata.image_url);
      if (mountedRef.current) {
        setImageUrl(parsedMetadata.image_url);
        setLoading(false);
      }
      return;
    }

    // Fallback to extracting media ID and downloading (for customer-sent WhatsApp images)
    console.log('📥 MediaImage: Attempting to extract media_id from metadata');
    const mediaId = whatsappMediaService.extractMediaIdFromMessage(message);
    console.log('🔍 MediaImage: Extracted media_id:', mediaId);

    if (!mediaId) {
      console.error('❌ MediaImage: No media ID found in message:', {
        messageId: message.id,
        metadata: message.metadata,
        media_url: message.media_url
      });
      if (mountedRef.current) {
        setError('No media ID found');
        setLoading(false);
      }
      return;
    }

    try {
      const result = await whatsappMediaService.downloadAndGetMediaUrl(
        tenantId, 
        mediaId, 
        'image',
        message.id
      );

      // Check if component is still mounted and this is the latest attempt
      if (!mountedRef.current || attemptId !== loadAttemptRef.current) {
        return;
      }

      if (result.success) {
        setImageUrl(result.fileUrl || null);
        setThumbnailUrl(result.thumbnailUrl || null);
        setError(null);
      } else {
        setError(result.error || 'Failed to load image');
      }
    } catch (err) {
      if (mountedRef.current && attemptId === loadAttemptRef.current) {
        setError('Failed to load image');
      }
    } finally {
      if (mountedRef.current && attemptId === loadAttemptRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadImage();
  }, [message.id, tenantId]); // Only depend on stable identifiers

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
        <Loader className="h-4 w-4 animate-spin" />
        <span className="text-sm">{t('media.loadingImage')}</span>
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
        <Image className="h-4 w-4" />
        <span className="text-sm">{message.content || '📷 Image'}</span>
        {error && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">({error})</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => loadImage(true)}
              className="h-6 w-6 p-0"
              title={t('media.retryImage')}
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2 w-full">
        <div className="relative w-full max-w-xs group">
          <img
            src={thumbnailUrl || imageUrl}
            alt={message.content || 'Image'}
            className="rounded-lg w-full h-auto max-h-[300px] object-cover cursor-pointer hover:opacity-90 transition-opacity"
            onClick={async (e) => {
              e.stopPropagation();
              // Refresh signed URL before zoom (may be expired after long session)
              if (message.media_url) {
                const fresh = await whatsappMediaService.getFreshSignedUrl(message.media_url);
                if (fresh && mountedRef.current) setImageUrl(fresh);
              }
              setShowZoomModal(true);
            }}
            onMouseDown={(e) => {
              // Prevent text selection from being interrupted by double-click
              if (e.detail > 1) e.preventDefault();
            }}
          />
        </div>
        
        {message.content && (
          <FormattedMessageContent content={message.content} className="text-sm text-muted-foreground" />
        )}
      </div>

      {/* Image Zoom Modal */}
      <ImageZoomModal
        isOpen={showZoomModal}
        onClose={() => setShowZoomModal(false)}
        imageUrl={imageUrl || ''}
        alt={message.content || 'Image'}
        messageId={message.id}
      />
    </>
  );
}

export const MediaImage = memo(MediaImageComponent);